/**
 * Mini-Angular Reactive Forms API in Pure TypeScript (Zero Dependencies)
 * Provides FormGroup, FormControl, and Validators with reactive subscriptions.
 */

type ValidatorFn = (value: any) => { [key: string]: any } | null;

interface ControlValueAccessor {
    writeValue(value: any): void;
    registerOnChange(fn: (value: any) => void): void;
    registerOnTouched(fn: () => void): void;
}

class DefaultValueAccessor implements ControlValueAccessor {
    constructor(private element: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) {}
    writeValue(value: any): void {
        this.element.value = value ?? '';
    }
    registerOnChange(fn: (value: any) => void): void {
        this.element.addEventListener('input', () => fn(this.element.value));
    }
    registerOnTouched(fn: () => void): void {
        this.element.addEventListener('blur', () => fn());
    }
}

class CheckboxValueAccessor implements ControlValueAccessor {
    constructor(private element: HTMLInputElement) {}
    writeValue(value: any): void {
        this.element.checked = !!value;
    }
    registerOnChange(fn: (value: any) => void): void {
        this.element.addEventListener('change', () => fn(this.element.checked));
    }
    registerOnTouched(fn: () => void): void {
        this.element.addEventListener('blur', () => fn());
    }
}

class FileValueAccessor implements ControlValueAccessor {
    private onChangeCb: (value: any) => void = () => {};
    private onTouchedCb: () => void = () => {};

    constructor(private element: HTMLInputElement) {
        this.setupUpload();
    }

    writeValue(value: any): void {
        if (!value) {
            this.element.value = '';
            const displayEl = this.element.parentElement?.querySelector('input[readonly]') as HTMLInputElement;
            if (displayEl) {
                displayEl.value = '';
            }
        }
    }

    registerOnChange(fn: (value: any) => void): void {
        this.onChangeCb = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouchedCb = fn;
    }

    private setupUpload() {
        const container = this.element.parentElement;
        const uploadBtn = container?.querySelector('button');
        const displayEl = container?.querySelector('input[readonly]') as HTMLInputElement;
        const isMultiple = this.element.hasAttribute('multiple');

        uploadBtn?.addEventListener('click', () => {
            this.element.click();
        });

        this.element.addEventListener('change', () => {
            const files = this.element.files;
            if (!files || files.length === 0) {
                if (displayEl) displayEl.value = '';
                this.onChangeCb('');
                return;
            }

            if (isMultiple) {
                // Múltiples archivos: mostrar conteo y enviar FileList
                if (displayEl) displayEl.value = `${files.length} archivo(s) seleccionado(s)`;
                this.onTouchedCb();
                this.onChangeCb(files);
            } else {
                // Un solo archivo: mostrar nombre
                const file = files[0];
                if (displayEl) displayEl.value = file.name;
                this.onTouchedCb();
                this.onChangeCb(file);
            }
        });
    }
}

class ValueAccessorRegistry {
    static getAccessor(element: any): ControlValueAccessor {
        if (element) {
            if (element.type === 'checkbox') {
                return new CheckboxValueAccessor(element);
            }
            if (element.type === 'file') {
                return new FileValueAccessor(element);
            }
        }
        return new DefaultValueAccessor(element);
    }
}

export class FormControl {
    private _value: any;
    private _validators: ValidatorFn[];
    private _errors: { [key: string]: any } | null = null;
    private _touched = false;
    private _dirty = false;
    private _valueChangesCallbacks: ((value: any) => void)[] = [];
    private _statusChangesCallbacks: ((status: 'VALID' | 'INVALID') => void)[] = [];

    constructor(initialValue: any = '', validators: ValidatorFn[] = []) {
        this._value = initialValue;
        this._validators = validators;
        this.validate();
    }

    get value(): any {
        return this._value;
    }

    set value(newValue: any) {
        if (this._value !== newValue) {
            this._value = newValue;
            this._dirty = true;
            this.validate();
            this._valueChangesCallbacks.forEach(cb => cb(this._value));
        }
    }

    get valid(): boolean {
        return this._errors === null;
    }

    get invalid(): boolean {
        return !this.valid;
    }

    get errors(): { [key: string]: any } | null {
        return this._errors;
    }

    get touched(): boolean {
        return this._touched;
    }

    get dirty(): boolean {
        return this._dirty;
    }

    markAsTouched() {
        this._touched = true;
        this.validate();
    }

    markAsUntouched() {
        this._touched = false;
        this._dirty = false;
        this.validate();
    }

    setValue(newValue: any) {
        this.value = newValue;
    }

    setErrors(errors: { [key: string]: any } | null) {
        this._errors = errors;
        this._statusChangesCallbacks.forEach(cb => cb(this.valid ? 'VALID' : 'INVALID'));
    }

    subscribe(callback: (value: any) => void): () => void {
        this._valueChangesCallbacks.push(callback);
        callback(this._value);
        return () => {
            this._valueChangesCallbacks = this._valueChangesCallbacks.filter(cb => cb !== callback);
        };
    }

    subscribeStatus(callback: (status: 'VALID' | 'INVALID') => void): () => void {
        this._statusChangesCallbacks.push(callback);
        callback(this.valid ? 'VALID' : 'INVALID');
        return () => {
            this._statusChangesCallbacks = this._statusChangesCallbacks.filter(cb => cb !== callback);
        };
    }

    private validate() {
        let mergedErrors: { [key: string]: any } | null = null;
        for (const validator of this._validators) {
            const err = validator(this._value);
            if (err) {
                mergedErrors = { ...(mergedErrors || {}), ...err };
            }
        }
        this._errors = mergedErrors;
        this._statusChangesCallbacks.forEach(cb => cb(this.valid ? 'VALID' : 'INVALID'));
    }
}

export class FormGroup {
    private _controls: { [key: string]: FormControl };
    private _statusChangesCallbacks: ((status: 'VALID' | 'INVALID') => void)[] = [];
    private _valueChangesCallbacks: (() => void)[] = [];

    constructor(controls: { [key: string]: FormControl }) {
        this._controls = controls;
        // Listen to child control value & status changes
        Object.keys(this._controls).forEach(key => {
            this._controls[key].subscribe(() => {
                this._valueChangesCallbacks.forEach(cb => cb());
            });
            this._controls[key].subscribeStatus(() => {
                this.notifyStatusChange();
            });
        });
    }

    get controls(): { [key: string]: FormControl } {
        return this._controls;
    }

    get(name: string): FormControl | null {
        return this._controls[name] || null;
    }

    get value(): { [key: string]: any } {
        const val: { [key: string]: any } = {};
        Object.keys(this._controls).forEach(key => {
            val[key] = this._controls[key].value;
        });
        return val;
    }

    get valid(): boolean {
        return Object.keys(this._controls).every(key => this._controls[key].valid);
    }

    get invalid(): boolean {
        return !this.valid;
    }

    reset() {
        Object.keys(this._controls).forEach(key => {
            const control = this._controls[key];
            if (typeof control.value === 'boolean') {
                control.setValue(false);
            } else {
                control.setValue('');
            }
            control.markAsUntouched();
        });
    }

    subscribeStatus(callback: (status: 'VALID' | 'INVALID') => void): () => void {
        this._statusChangesCallbacks.push(callback);
        callback(this.valid ? 'VALID' : 'INVALID');
        return () => {
            this._statusChangesCallbacks = this._statusChangesCallbacks.filter(cb => cb !== callback);
        };
    }

    subscribe(callback: () => void): () => void {
        this._valueChangesCallbacks.push(callback);
        return () => {
            this._valueChangesCallbacks = this._valueChangesCallbacks.filter(cb => cb !== callback);
        };
    }

    private notifyStatusChange() {
        this._statusChangesCallbacks.forEach(cb => cb(this.valid ? 'VALID' : 'INVALID'));
    }

    markAllAsTouched() {
        Object.keys(this._controls).forEach(key => {
            this._controls[key].markAsTouched();
        });
    }

    bindToDOM(container: HTMLElement, customMessages?: { [controlName: string]: { [errorKey: string]: string } }) {
        if (!container) return;

        // 1. Vincular todos los inputs con atributo [formControlName]
        const inputs = container.querySelectorAll('[formControlName]');
        inputs.forEach((inputEl: any) => {
            const controlName = inputEl.getAttribute('formControlName');
            if (!controlName) return;

            const control = this.get(controlName);
            if (!control) return;

            const accessor = ValueAccessorRegistry.getAccessor(inputEl);

            // Sincronizar estado del control -> DOM
            accessor.writeValue(control.value);
            control.subscribe(val => {
                accessor.writeValue(val);
            });

            // Registrar callbacks para eventos DOM -> Control
            accessor.registerOnChange(val => {
                control.value = val;
            });

            accessor.registerOnTouched(() => {
                control.markAsTouched();
            });
            
            // Buscar elemento de error con atributo [formErrorFor="controlName"]
            const errorEl = container.querySelector(`[formErrorFor="${controlName}"]`);

            // Suscribirse a cambios del control para actualizar UI de error automáticamente
            control.subscribeStatus(() => {
                if (control.invalid && (control.touched || control.dirty)) {
                    inputEl.classList.add('border-red-500', 'focus:border-red-500');

                    if (errorEl) {
                        let errorMessage = "";
                        const errors = control.errors || {};

                        for (const key of Object.keys(errors)) {
                            if (customMessages && customMessages[controlName] && customMessages[controlName][key]) {
                                errorMessage = customMessages[controlName][key];
                                break;
                            }
                        }

                        if (!errorMessage) {
                            if (errors.required) {
                                errorMessage = "Este campo es obligatorio.";
                            } else if (errors.requiredTrue) {
                                errorMessage = "Debe ser marcado.";
                            } else if (errors.email) {
                                errorMessage = "El formato de correo es inválido.";
                            } else if (errors.minlength) {
                                errorMessage = `Debe tener al menos ${errors.minlength.requiredLength} caracteres.`;
                            } else if (errors.maxlength) {
                                errorMessage = `Debe tener máximo ${errors.maxlength.requiredLength} caracteres.`;
                            } else if (errors.pattern) {
                                errorMessage = "Formato inválido.";
                            }
                        }

                        errorEl.textContent = errorMessage;
                    }
                } else {
                    inputEl.classList.remove('border-red-500', 'focus:border-red-500');
                    if (errorEl) {
                        errorEl.textContent = "";
                    }
                }
            });
        });

        // 2. Vincular el botón de enviar (submit) del contenedor
        const submitBtn = container.querySelector('[type="submit"]') as HTMLButtonElement;
        if (submitBtn) {
            this.subscribeStatus((status) => {
                const isValid = status === 'VALID';
                submitBtn.disabled = !isValid;
                if (isValid) {
                    submitBtn.classList.remove('opacity-50', 'cursor-not-allowed');
                } else {
                    submitBtn.classList.add('opacity-50', 'cursor-not-allowed');
                }
            });
        }
    }
}

export class Validators {
    static required(controlVal: any) {
        if (controlVal === null || controlVal === undefined || controlVal === '') {
            return { required: true };
        }
        return null;
    }

    static requiredTrue(controlVal: any) {
        return controlVal === true ? null : { requiredTrue: true };
    }

    static email(controlVal: any) {
        if (!controlVal) return null; // Let 'required' handle empty values
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(controlVal) ? null : { email: true };
    }

    static minLength(length: number) {
        return (controlVal: any) => {
            if (!controlVal) return null;
            return controlVal.length >= length ? null : { minlength: { requiredLength: length, actualLength: controlVal.length } };
        };
    }

    static maxLength(length: number) {
        return (controlVal: any) => {
            if (!controlVal) return null;
            return controlVal.length <= length ? null : { maxlength: { requiredLength: length, actualLength: controlVal.length } };
        };
    }

    static pattern(regex: RegExp) {
        return (controlVal: any) => {
            if (!controlVal) return null;
            return regex.test(controlVal) ? null : { pattern: true };
        };
    }
}

// Attach to window global namespace for ESM resolution-free usage in standard MVC script inclusions
if (typeof window !== 'undefined') {
    (window as any).XploraForms = {
        FormControl,
        FormGroup,
        Validators
    };
}
